/**
 * Tooltip markup for the Cabinet graph.
 *
 * SECURITY: ECharts renders a tooltip `formatter` string through `innerHTML`.
 * Every value that reaches these builders is catalog data derived by the
 * classifier from uploaded document BODIES (titles, doc types, entity types,
 * periods, evidence lines) — i.e. attacker-controllable by anyone who can put a
 * file in a deal room. A crafted in-body title would otherwise execute script on
 * hover (stored XSS).
 *
 * So: this module is the ONLY place that builds tooltip HTML, every dynamic
 * value goes through `escapeHtml`, and the structural tags are literals here.
 * Do not interpolate catalog strings into an ECharts `formatter` anywhere else.
 */

import type { CabinetEdge, CabinetNode } from "../../types/cabinet";
import { EDGE_TYPE_STYLES, NODE_KIND_STYLES } from "./cabinetTheme";

/**
 * Escape the five characters that can break out of HTML text or an attribute
 * value. `&` must be replaced first or it would double-escape the others.
 */
/**
 * Flatten edge evidence to one line for the tooltip.
 *
 * Objects are rendered as `key: value` pairs rather than raw JSON: a
 * `same_content` edge's evidence is the two relpaths and a hash, and a reader
 * hovering the line wants to read them, not parse braces. Still a single
 * escaped string — see the security note above.
 */
export function evidenceText(
	evidence: Record<string, unknown> | string,
): string {
	if (typeof evidence === "string") return evidence;
	return Object.entries(evidence)
		.map(([key, value]) => {
			const rendered = Array.isArray(value)
				? value.join(", ")
				: String(value ?? "");
			return `${key}: ${rendered}`;
		})
		.join(" · ");
}

export function escapeHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

/** Node hover card: label, kind, then whatever classification detail exists. */
export function nodeTooltipHtml(node: CabinetNode): string {
	const lines: string[] = [`<strong>${escapeHtml(node.label)}</strong>`];
	lines.push(escapeHtml(NODE_KIND_STYLES[node.kind].label));
	if (node.doc_type) lines.push(escapeHtml(node.doc_type));
	if (node.meta.entity_type) lines.push(escapeHtml(node.meta.entity_type));
	if (node.meta.period) lines.push(escapeHtml(node.meta.period));
	return lines.join("<br/>");
}

/** Edge hover card: the edge type, plus the evidence that justified it. */
export function edgeTooltipHtml(edge: CabinetEdge): string {
	const label = escapeHtml(EDGE_TYPE_STYLES[edge.type].label);
	if (!edge.evidence) return label;
	return `${label}<br/>${escapeHtml(evidenceText(edge.evidence))}`;
}
